import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { connectStayAiIntegration } from "./schema/connect-stay-ai-integration";
import { connectStripeIntegration } from "./schema/connect-stripe-integration";
import { disconnectGorgiasIntegration } from "./schema/disconnect-gorgias-integration";
import { disconnectPayPalIntegration } from "./schema/disconnect-paypal-integration";
import { disconnectStayAiIntegration } from "./schema/disconnect-stay-ai-integration";
import { disconnectStripeIntegration } from "./schema/disconnect-stripe-integration";
import { enableDisputes } from "./schema/enable-disputes";
import { enableDisputesBilling } from "./schema/enable-disputes-billing";
import { enrollDisputelyAlerts } from "./schema/enroll-disputely-alerts";
import { enrollDisputelyResolve } from "./schema/enroll-disputely-resolve";
import { generateAllEvidence } from "./schema/generate-all-evidence";
import { getAlertCatchRate } from "./schema/get-alert-catch-rate";
import { getAlertProviderVolume } from "./schema/get-alert-provider-volume";
import { getAlertStats } from "./schema/get-alert-stats";
import { getDeflection } from "./schema/get-deflection";
import { getDispute } from "./schema/get-dispute";
import { getDisputeCoverageStats } from "./schema/get-dispute-coverage-stats";
import { getDisputeDashboardStats } from "./schema/get-dispute-dashboard-stats";
import { getDisputeGorgiasTickets } from "./schema/get-dispute-gorgias-tickets";
import { getDisputeMethodStats } from "./schema/get-dispute-method-stats";
import { getDisputeProductStats } from "./schema/get-dispute-product-stats";
import { getDisputeRatio } from "./schema/get-dispute-ratio";
import { getDisputelyAlert } from "./schema/get-disputely-alert";
import { getDisputelyAlertOrder } from "./schema/get-disputely-alert-order";
import { getDisputelyAlertStatus } from "./schema/get-disputely-alert-status";
import { getDisputelyAutofill } from "./schema/get-disputely-autofill";
import { getDisputelyEnrollPrefill } from "./schema/get-disputely-enroll-prefill";
import { getDisputelyResolvePrefill } from "./schema/get-disputely-resolve-prefill";
import { getDisputelyResolveStatus } from "./schema/get-disputely-resolve-status";
import { getDisputes } from "./schema/get-disputes";
import { getGorgiasConnectionStatus } from "./schema/get-gorgias-connection-status";
import { getPayPalAuthUrl } from "./schema/get-paypal-auth-url";
import { getPayPalConnectionStatus } from "./schema/get-paypal-connection-status";
import { getReclaimAccessDefaults } from "./schema/get-reclaim-access-defaults";
import { getReclaimProfileDefaults } from "./schema/get-reclaim-profile-defaults";
import { getResolveStats } from "./schema/get-resolve-stats";
import { getShopPolicies } from "./schema/get-shop-policies";
import { getShopifyPermissionsUpdateUrl } from "./schema/get-shopify-permissions-update-url";
import { getStayAiConnectionStatus } from "./schema/get-stay-ai-connection-status";
import { getStripeConnectionStatus } from "./schema/get-stripe-connection-status";
import { getTeamPartnerAttribution } from "./schema/get-team-partner-attribution";
import { getUnifiedReasonStats } from "./schema/get-unified-reason-stats";
import { listDeflections } from "./schema/list-deflections";
import { listDisputelyAlerts } from "./schema/list-disputely-alerts";
import { recordChargebackOnboardingSelection } from "./schema/record-chargeback-onboarding-selection";
import { recordReclaimCampaignTouch } from "./schema/record-reclaim-campaign-touch";
import { redeemReclaimPartnerCode } from "./schema/redeem-reclaim-partner-code";
import { submitDisputeEvidence } from "./schema/submit-dispute-evidence";
import { toggleDisputeAutoEvidence } from "./schema/toggle-dispute-auto-evidence";
import { unenrollDisputelyResolve } from "./schema/unenroll-disputely-resolve";
import { updateDeflectionSettings } from "./schema/update-deflection-settings";
import { updateDisputeAlertAutomation } from "./schema/update-dispute-alert-automation";
import { updateDisputeEvidence } from "./schema/update-dispute-evidence";
import { updateReclaimProfile } from "./schema/update-reclaim-profile";
import { updateShopPolicy } from "./schema/update-shop-policy";
import { validateReclaimPartnerCode } from "./schema/validate-reclaim-partner-code";

export const disputeMerchantRpcDefinition = {
  enableDisputes,
  enableDisputesBilling,
  enrollDisputelyAlerts,
  enrollDisputelyResolve,
  unenrollDisputelyResolve,
  getAlertStats,
  getDisputeCoverageStats,
  getDisputeMethodStats,
  getDisputeProductStats,
  getUnifiedReasonStats,
  getAlertCatchRate,
  getAlertProviderVolume,
  getResolveStats,
  getDeflection,
  getDispute,
  getDisputeDashboardStats,
  getDisputeRatio,
  getDisputelyAlert,
  getDisputelyAlertOrder,
  getDisputelyAlertStatus,
  getDisputelyAutofill,
  getDisputelyEnrollPrefill,
  getDisputelyResolvePrefill,
  getDisputelyResolveStatus,
  getDisputes,
  listDisputelyAlerts,
  updateDisputeAlertAutomation,
  listDeflections,
  getShopPolicies,
  submitDisputeEvidence,
  updateDisputeEvidence,
  updateDeflectionSettings,
  updateReclaimProfile,
  updateShopPolicy,
  generateAllEvidence,
  toggleDisputeAutoEvidence,
  getPayPalAuthUrl,
  getReclaimAccessDefaults,
  getReclaimProfileDefaults,
  recordReclaimCampaignTouch,
  getShopifyPermissionsUpdateUrl,
  getPayPalConnectionStatus,
  disconnectPayPalIntegration,
  getStripeConnectionStatus,
  connectStripeIntegration,
  disconnectStripeIntegration,
  getGorgiasConnectionStatus,
  disconnectGorgiasIntegration,
  getDisputeGorgiasTickets,
  getStayAiConnectionStatus,
  connectStayAiIntegration,
  disconnectStayAiIntegration,
  redeemReclaimPartnerCode,
  validateReclaimPartnerCode,
  getTeamPartnerAttribution,
  recordChargebackOnboardingSelection,
} as const;

export type DisputeMerchantRpcDefinition = InferRpcDefinition<
  typeof disputeMerchantRpcDefinition
>;
